import { useEffect, useState } from 'react';
import { Header, Table, Message, Button, Toast, Modal } from 'react-frontier';
import { Icon } from 'semantic-ui-react';
import { RFCWorker } from '../SiteClasses';
import { useUser } from '../hooks';
import classNames from 'classnames';
import UserAccess from '../UserAccess';
import API from '../API';

interface DownloadRFCReportProps{
	open: boolean,
	onClose: ()=>void,
	onComplete?: (worker_ids: number[])=>void,
}

var DownloadRFCReport = (props: DownloadRFCReportProps)=>{
	var { hasAccess } = useUser();
	var [workers, setWorkers] = useState<RFCWorker[]>(null);
	var [selectedIds, setSelectedIds] = useState<number[]>([]);
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);
	var [loading, setLoading] = useState<boolean>(false);
	var [confirmModal, setConfirmModal] = useState<boolean>(false);

	useEffect(()=>{
		if(!props.open) return;
		if(!hasAccess(UserAccess.WORKERS.VALIDATE_RFC)) return;
		setSelectedIds([]);
		setModalPrompts(null);
		setConfirmModal(false);
		get();
	}, [props.open]);

	var get = ()=>{
		setWorkers(null);
		API.getEmployeesRFCList().then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			setWorkers(res.data || []);
		}).catch(()=>{
			setModalPrompts(['Hubo un error cargando los trabajadores. (RFC-1)']);
			setWorkers([]);
		});
	};

	var toggleWorker = (worker_id: number)=>{
		setSelectedIds(prev=>{
			var idx = prev.indexOf(worker_id);
			if(idx === -1) return [...prev, worker_id];
			return prev.filter(id=>id !== worker_id);
		});
	};

	var toggleAll = ()=>{
		if(!workers) return;
		if(selectedIds.length === workers.length){
			setSelectedIds([]);
		}else{
			setSelectedIds(workers.map(w=>w.worker_id));
		}
	};

	var onDownload = async()=>{
		if(selectedIds.length === 0) return setModalPrompts(['Selecciona al menos un trabajador.']);
		setLoading(true);
		setModalPrompts(null);

		try{
			var res = await API.downloadEmployeesRFCReport(selectedIds);
			if(res?.error){
				setModalPrompts([res.message]);
				setLoading(false);
				return;
			}
			setConfirmModal(true);
		}catch{
			setModalPrompts(['Hubo un error descargando el reporte. (RFC-2)']);
		}finally{
			setLoading(false);
		}
	};

	var onConfirm = async()=>{
		setLoading(true);
		setModalPrompts(null);

		try{
			var res = await API.validateWorkerRFC(selectedIds);
			if(res?.error){
				setModalPrompts([res.message]);
				setLoading(false);
				return;
			}
			Toast.success('Trabajadores marcados como RFC validado.');
			setConfirmModal(false);
			props.onClose();
			if(props.onComplete) props.onComplete(selectedIds);
		}catch{
			setModalPrompts(['Hubo un error validando los RFC. (RFC-3)']);
		}finally{
			setLoading(false);
		}
	};

	var onClose = ()=>{
		if(confirmModal) return;
		props.onClose();
	};

	if(!hasAccess(UserAccess.WORKERS.VALIDATE_RFC)) return null;

	return <div>
		<Modal open={props.open && !confirmModal} size='small' onClose={onClose}>
			<Modal.Header>Reporte validación RFC</Modal.Header>
			{workers === null ? (
				<Modal.Content>
					<Header loading text='Cargando trabajadores...' />
				</Modal.Content>
			) : workers.length === 0 ? (
				<Modal.Content>
					<Header text='Sin trabajadores' subtext='No hay trabajadores pendientes de validar RFC.' />
					<Message type='error' list={modalPrompts} />
				</Modal.Content>
			) : (
				<Modal.Content style={{ maxHeight: 500, overflowY: 'auto', overflowX: 'hidden' }}>
					<div style={{ textAlign: 'center', marginBottom: 10 }}>
						<Button text={selectedIds.length === workers.length ? 'Deseleccionar todos' : 'Seleccionar todos'} color='black' onClick={toggleAll} />
					</div>
					<Table
						selectable
						fitted
						collapsingIndexes={[0, 1]}
						onClick={(v: any[])=>{
							toggleWorker(v[1]);
						}}
						headers={[
							<div style={{ textAlign: 'center' }}>
								<i className="checkmark icon" style={{ marginRight: 0 }}></i>
							</div>,
							'ID',
							'Nombre',
							'RFC',
						]}
						data={workers.map(w=>{
							var selected = selectedIds.indexOf(w.worker_id) !== -1;
							return [
								<i className={classNames('large icon circle', {
									check: selected,
									outline: !selected,
								})}></i>,
								w.worker_id,
								`${w.first_name} ${w.last_name}`,
								w.rfc,
							];
						})}
					/>
					<Message type='error' list={modalPrompts} />
				</Modal.Content>
			)}
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={onClose} />
				<Button text='Descargar' color='green' onClick={onDownload} loading={loading} disabled={!workers || workers.length === 0 || selectedIds.length === 0} />
			</Modal.Actions>
		</Modal>
		<Modal open={confirmModal} size='mini'>
			<Modal.Header>Confirmar descarga</Modal.Header>
			<Modal.Content>
				<Header text='Se ha descargado el reporte de validación RFC.' />
				<Message type='warning' centered>
					<div style={{ fontSize: 20, fontWeight: 'bold', marginBottom: 10 }}>Confirmar descarga</div>
					<div>Al confirmar, los <b>{selectedIds.length}</b> trabajador(es) seleccionado(s) serán marcados como <b>RFC validado</b>.</div>
					<Button style={{ marginTop: 15 }} text='Confirmar descarga' color='black' onClick={onConfirm} loading={loading} />
				</Message>
				<div style={{ textAlign: 'center', padding: 10 }}>
					<Button text='Reintentar descarga' basic onClick={onDownload} loading={loading} />
				</div>
				<Message list={modalPrompts} type='error' />
			</Modal.Content>
		</Modal>
	</div>;
};

export default DownloadRFCReport;
